import React from "react";
import { Card } from "@/components/ui/card";
import { Network, Shield, Cloud, Zap, CheckCircle2, ArrowRight } from "lucide-react";

const IntegrationEcosystemSection = () => {
  const spharakaProducts = [
    {
      icon: Shield,
      name: "Spharaka XDR",
      description: "Deep detection and response"
    },
    {
      icon: Network,
      name: "Spharaka EDR & UEBA",
      description: "Endpoint and behavioral intelligence"
    },
    {
      icon: CheckCircle2,
      name: "Spharaka Zero Trust",
      description: "Continuous verification and policy enforcement"
    }
  ];

  return (
    <section className="py-16 md:py-20 bg-gradient-to-b from-muted/30 to-background relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute top-20 left-10 w-72 h-72 bg-[#8932c8]/10 rounded-full blur-3xl"></div>
      <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"></div>
      
      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-6xl mx-auto space-y-12">
          <div className="text-center space-y-4">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-foreground via-[#8932c8] to-foreground bg-clip-text text-transparent">
              Integration Ecosystem
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              AuraXP seamlessly integrates across your entire security infrastructure
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Spharaka Products Card */}
            <Card className="p-8 bg-gradient-to-br from-background via-background to-muted/30 border-2 border-muted hover:border-[#8932c8]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#8932c8]/10 group">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#8932c8] to-primary flex items-center justify-center">
                    <Network className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Spharaka Products</h3>
                </div>

                <div className="space-y-4">
                  {spharakaProducts.map((product, index) => (
                    <div 
                      key={index} 
                      className="flex items-start gap-4 p-4 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors group/item"
                    >
                      <div className="w-10 h-10 rounded-lg bg-[#8932c8]/10 flex items-center justify-center flex-shrink-0 group-hover/item:bg-[#8932c8]/20 transition-colors">
                        <product.icon className="w-5 h-5 text-[#8932c8]" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-semibold text-foreground mb-1">{product.name}</h4>
                        <p className="text-sm text-muted-foreground">{product.description}</p>
                      </div>
                      <ArrowRight className="w-5 h-5 text-[#8932c8] opacity-0 group-hover/item:opacity-100 transition-opacity" />
                    </div>
                  ))}
                </div>
              </div>
            </Card>

            {/* Third-Party Platforms Card */}
            <Card className="p-8 bg-gradient-to-br from-background via-background to-muted/30 border-2 border-muted hover:border-[#8932c8]/50 transition-all duration-300 hover:shadow-xl hover:shadow-[#8932c8]/10 group">
              <div className="space-y-6">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-[#8932c8] flex items-center justify-center">
                    <Cloud className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">Third-Party Platforms</h3>
                </div>

                <div className="space-y-4">
                  <p className="text-muted-foreground leading-relaxed">
                    Connects with leading SIEM, SOAR, and cloud-native platforms to form a unified, intelligent defense fabric that scales with your enterprise.
                  </p>

                  <div className="grid grid-cols-2 gap-3 pt-4">
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <Zap className="w-4 h-4 text-[#8932c8]" />
                      <span className="text-sm font-medium text-foreground">SIEM</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <Zap className="w-4 h-4 text-[#8932c8]" />
                      <span className="text-sm font-medium text-foreground">SOAR</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <Zap className="w-4 h-4 text-[#8932c8]" />
                      <span className="text-sm font-medium text-foreground">Cloud Native</span>
                    </div>
                    <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors">
                      <Zap className="w-4 h-4 text-[#8932c8]" />
                      <span className="text-sm font-medium text-foreground">APIs</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Bottom integration note */}
          <div className="text-center">
            <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#8932c8]/10 border border-[#8932c8]/20">
              <CheckCircle2 className="w-5 h-5 text-[#8932c8]" />
              <span className="text-sm font-medium text-foreground">Seamless Integration • Zero Friction • Maximum Visibility</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IntegrationEcosystemSection;
